import {
	type BalanceSheetId,
	Dtos,
	type FinancialPeriod,
	FinancialPeriodPreparationMethod,
} from "@inrev/common";
import { DateTime } from "luxon";
import {
	type BooleanCell,
	buildPeriodRecord,
	type FinancialViewCell,
	type FinancialViewRow,
	type PeriodViewModel,
	type PpChangeColorMode,
	type StatementMatrixViewModel,
} from "../../ui/FinancialDataGrid";
import { BalanceSheetWithView } from "../types";
import { FinancialPeriodDto } from "../utils";
import { balanceSheetTypeLabel } from "./financialStatementSlots";

type BalanceSheet = Dtos.Admin.BalanceSheet.Get.Response;

const periodTypeLabel = (sheet: BalanceSheet): string => {
	const type = balanceSheetTypeLabel(sheet.statementType);
	const method = FinancialPeriodPreparationMethod.displayName(sheet.preparationMethod);
	return `${type} | ${method}`;
};

type GroupAccessor = (view: FinancialPeriod.BalanceSheetView) => FinancialPeriod.LineItemGroupView;
type RollupAccessor = (view: FinancialPeriod.BalanceSheetView) => FinancialPeriod.RollupView;

type GroupSpec = {
	id: string;
	label: string;
	accessor: GroupAccessor;
	automated?: boolean;
};

type CategorySpec = {
	id: string;
	label: string;
	groups: GroupSpec[];
	totalAccessor: RollupAccessor;
};

type SectionSpec = {
	id: string;
	label: string;
	subtotalId: string;
	subtotalLabel: string;
	categories: CategorySpec[];
	totalAccessor: RollupAccessor;
};

const TOTAL_ASSETS_ID = "bs::total-assets";
const TOTAL_LIABILITIES_ID = "bs::total-liabilities";
const TOTAL_EQUITY_ID = "bs::equity::main::__subtotal";
const TOTAL_LIABILITIES_AND_EQUITY_ID = "bs::total-liabilities-and-equity";
const BALANCED_ID = "bs::balanced";

const LIABILITIES_SECTION_ID = "bs::liabilities";

/**
 * Automated groups whose period-over-period change carries no favorable/unfavorable signal, so their
 * change badge is always rendered gray (see {@link PpChangeColorMode}).
 */
const NEUTRAL_GROUP_IDS: ReadonlySet<string> = new Set([
	"currentAssetsDropped",
	"currentLiabilitiesDropped",
	"equityAdjustments",
]);

/**
 * Coloring for a section's rows: liabilities are lower-is-better (a decrease is favorable — green),
 * everything else is higher-is-better (an increase is favorable). Individual groups can override this to
 * "neutral".
 */
const sectionColorMode = (sectionId: string): PpChangeColorMode =>
	sectionId === LIABILITIES_SECTION_ID ? "lowerIsBetter" : "higherIsBetter";

const SECTIONS: readonly SectionSpec[] = [
	{
		id: "bs::assets",
		label: "ASSETS",
		subtotalId: TOTAL_ASSETS_ID,
		subtotalLabel: "Total Assets",
		totalAccessor: (v) => v.assets.total,
		categories: [
			{
				id: "bs::assets::current",
				label: "Current Assets",
				totalAccessor: (v) => v.assets.current.total,
				groups: [
					{ id: "cash", label: "Cash", accessor: (d) => d.assets.current.cash },
					{
						id: "restrictedCash",
						label: "Restricted Cash",
						accessor: (d) => d.assets.current.restrictedCash,
					},
					{
						id: "marketableSecurities",
						label: "Marketable Securities",
						accessor: (d) => d.assets.current.marketableSecurities,
					},
					{
						id: "accountsReceivable",
						label: "Accounts Receivable",
						accessor: (d) => d.assets.current.accountsReceivable,
					},
					{
						id: "contractAssets",
						label: "Contract Assets",
						accessor: (d) => d.assets.current.contractAssets,
					},
					{ id: "retainage", label: "Retainage", accessor: (d) => d.assets.current.retainage },
					{
						id: "underbillings",
						label: "Underbillings",
						accessor: (d) => d.assets.current.underbillings,
					},
					{ id: "inventory", label: "Inventory", accessor: (d) => d.assets.current.inventory },
					{ id: "prepaids", label: "Prepaids", accessor: (d) => d.assets.current.prepaids },
					{
						id: "currentShareholderEmplReceivables",
						label: "Current Shareholder/Employee Receivables",
						accessor: (d) => d.assets.current.currentShareholderEmplReceivables,
					},
					{
						id: "relatedPartyReceivables",
						label: "Related Party Receivables",
						accessor: (d) => d.assets.current.relatedPartyReceivables,
					},
					{
						id: "otherCurrentAssets",
						label: "Other Current Assets",
						accessor: (d) => d.assets.current.otherCurrentAssets,
					},
				],
			},
			{
				id: "bs::assets::fixed",
				label: "Fixed Assets",
				totalAccessor: (v) => v.assets.fixed.total,
				groups: [
					{
						id: "generalFixedAssets",
						label: "General Fixed Assets",
						accessor: (d) => d.assets.fixed.generalFixedAssets,
					},
					{ id: "realEstate", label: "Real Estate", accessor: (d) => d.assets.fixed.realEstate },
					{ id: "equipment", label: "Equipment", accessor: (d) => d.assets.fixed.equipment },
					{
						id: "officeEquipmentFurnitureFixtures",
						label: "Office Equipment, Furniture & Fixtures",
						accessor: (d) => d.assets.fixed.officeEquipmentFurnitureFixtures,
					},
					{ id: "software", label: "Software", accessor: (d) => d.assets.fixed.software },
					{
						id: "accumulatedDepreciation",
						label: "Accumulated Depreciation",
						accessor: (d) => d.assets.fixed.accumulatedDepreciation,
					},
					{
						id: "otherFixedAssets",
						label: "Other Fixed Assets",
						accessor: (d) => d.assets.fixed.otherFixedAssets,
					},
				],
			},
			{
				id: "bs::assets::nonCurrent",
				label: "Non-Current Assets",
				totalAccessor: (v) => v.assets.nonCurrent.total,
				groups: [
					{
						id: "otherReceivables",
						label: "Other Receivables",
						accessor: (d) => d.assets.nonCurrent.otherReceivables,
					},
					{
						id: "relatedPartyReceivables",
						label: "Related Party Receivables",
						accessor: (d) => d.assets.nonCurrent.relatedPartyReceivables,
					},
					{
						id: "nonCurrentShareholderEmplReceivables",
						label: "Non-Current Shareholder/Employee Receivables",
						accessor: (d) => d.assets.nonCurrent.nonCurrentShareholderEmplReceivables,
					},
					{ id: "deposits", label: "Deposits", accessor: (d) => d.assets.nonCurrent.deposits },
					{ id: "rouAssets", label: "ROU Assets", accessor: (d) => d.assets.nonCurrent.rouAssets },
					{ id: "goodwill", label: "Goodwill", accessor: (d) => d.assets.nonCurrent.goodwill },
					{
						id: "intangibles",
						label: "Intangibles",
						accessor: (d) => d.assets.nonCurrent.intangibles,
					},
					{
						id: "investment",
						label: "Investment",
						accessor: (d) => d.assets.nonCurrent.investment,
					},
					{
						id: "otherNonCurrentAssets",
						label: "Other Non-Current Assets",
						accessor: (d) => d.assets.nonCurrent.otherNonCurrentAssets,
					},
					{
						id: "currentAssetsDropped",
						label: "Current Assets Dropped",
						accessor: (d) => d.assets.nonCurrent.currentAssetsDropped,
						automated: true,
					},
				],
			},
		],
	},
	{
		id: "bs::liabilities",
		label: "LIABILITIES",
		subtotalId: TOTAL_LIABILITIES_ID,
		subtotalLabel: "Total Liabilities",
		totalAccessor: (v) => v.liabilities.total,
		categories: [
			{
				id: "bs::liabilities::current",
				label: "Current Liabilities",
				totalAccessor: (v) => v.liabilities.current.total,
				groups: [
					{
						id: "accountsPayable",
						label: "Accounts Payable",
						accessor: (d) => d.liabilities.current.accountsPayable,
					},
					{
						id: "contractLiabilities",
						label: "Contract Liabilities",
						accessor: (d) => d.liabilities.current.contractLiabilities,
					},
					{
						id: "retainagePayable",
						label: "Retainage Payable",
						accessor: (d) => d.liabilities.current.retainagePayable,
					},
					{
						id: "overbillings",
						label: "Overbillings",
						accessor: (d) => d.liabilities.current.overbillings,
					},
					{
						id: "customerDeposits",
						label: "Customer Deposits",
						accessor: (d) => d.liabilities.current.customerDeposits,
					},
					{
						id: "accruedExpenses",
						label: "Accrued Expenses",
						accessor: (d) => d.liabilities.current.accruedExpenses,
					},
					{
						id: "accruedLossesOnContracts",
						label: "Accrued Losses on Contracts",
						accessor: (d) => d.liabilities.current.accruedLossesOnContracts,
					},
					{
						id: "creditCardsBalance",
						label: "Credit Cards Balance",
						accessor: (d) => d.liabilities.current.creditCardsBalance,
					},
					{
						id: "cashOverdraft",
						label: "Cash Overdraft",
						accessor: (d) => d.liabilities.current.cashOverdraft,
					},
					{
						id: "blocBalance",
						label: "BLOC Balance",
						accessor: (d) => d.liabilities.current.blocBalance,
					},
					{
						id: "currentPortionOfLtd",
						label: "Current Portion of LTD",
						accessor: (d) => d.liabilities.current.currentPortionOfLtd,
					},
					{
						id: "otherNotesPayable",
						label: "Other Notes Payable",
						accessor: (d) => d.liabilities.current.otherNotesPayable,
					},
					{
						id: "taxesPayable",
						label: "Taxes Payable",
						accessor: (d) => d.liabilities.current.taxesPayable,
					},
					{
						id: "taxesDeferred",
						label: "Taxes Deferred",
						accessor: (d) => d.liabilities.current.taxesDeferred,
					},
					{
						id: "relatedPartyPayable",
						label: "Related Party Payable",
						accessor: (d) => d.liabilities.current.relatedPartyPayable,
					},
					{
						id: "shareholderEmplPayable",
						label: "Shareholder/Employee Payable",
						accessor: (d) => d.liabilities.current.shareholderEmplPayable,
					},
					{
						id: "currentPortionOfRouLiabilities",
						label: "Current Portion of ROU Liabilities",
						accessor: (d) => d.liabilities.current.currentPortionOfRouLiabilities,
					},
					{
						id: "otherCurrentLiabilities",
						label: "Other Current Liabilities",
						accessor: (d) => d.liabilities.current.otherCurrentLiabilities,
					},
				],
			},
			{
				id: "bs::liabilities::nonCurrent",
				label: "Non-Current Liabilities",
				totalAccessor: (v) => v.liabilities.nonCurrent.total,
				groups: [
					{
						id: "ltdBankTermLoan",
						label: "LTD Bank Term Loan",
						accessor: (d) => d.liabilities.nonCurrent.ltdBankTermLoan,
					},
					{
						id: "ltdRealEstate",
						label: "LTD Real Estate",
						accessor: (d) => d.liabilities.nonCurrent.ltdRealEstate,
					},
					{
						id: "ltdEquipment",
						label: "LTD Equipment",
						accessor: (d) => d.liabilities.nonCurrent.ltdEquipment,
					},
					{
						id: "ltdOther",
						label: "LTD Other",
						accessor: (d) => d.liabilities.nonCurrent.ltdOther,
					},
					{
						id: "ltdShareholder",
						label: "LTD Shareholder",
						accessor: (d) => d.liabilities.nonCurrent.ltdShareholder,
					},
					{
						id: "ltdRelatedParty",
						label: "LTD Related Party",
						accessor: (d) => d.liabilities.nonCurrent.ltdRelatedParty,
					},
					{
						id: "taxesDeferred",
						label: "Taxes Deferred",
						accessor: (d) => d.liabilities.nonCurrent.taxesDeferred,
					},
					{
						id: "ltPortionOfRouLiabilities",
						label: "LT Portion of ROU Liabilities",
						accessor: (d) => d.liabilities.nonCurrent.ltPortionOfRouLiabilities,
					},
					{
						id: "ltPortionOfAccruedLossesOnContracts",
						label: "LT Portion of Accrued Losses on Contracts",
						accessor: (d) => d.liabilities.nonCurrent.ltPortionOfAccruedLossesOnContracts,
					},
					{
						id: "otherLtLiabilities",
						label: "Other LT Liabilities",
						accessor: (d) => d.liabilities.nonCurrent.otherLtLiabilities,
					},
					{
						id: "currentLiabilitiesDropped",
						label: "Current Liabilities Dropped",
						accessor: (d) => d.liabilities.nonCurrent.currentLiabilitiesDropped,
						automated: true,
					},
				],
			},
		],
	},
	{
		id: "bs::equity",
		label: "EQUITY",
		subtotalId: TOTAL_EQUITY_ID,
		subtotalLabel: "Total Equity",
		totalAccessor: (v) => v.equity.total,
		categories: [
			{
				id: "bs::equity::main",
				label: "Equity",
				totalAccessor: (v) => v.equity.total,
				groups: [
					{
						id: "stocksIssuedAndOutstanding",
						label: "Stocks Issued and Outstanding",
						accessor: (d) => d.equity.stocksIssuedAndOutstanding,
					},
					{
						id: "treasuryStock",
						label: "Treasury Stock",
						accessor: (d) => d.equity.treasuryStock,
					},
					{
						id: "paidInCapital",
						label: "Paid-In Capital",
						accessor: (d) => d.equity.paidInCapital,
					},
					{
						id: "retainedEarnings",
						label: "Retained Earnings",
						accessor: (d) => d.equity.retainedEarnings,
					},
					{
						id: "currentYearEarnings",
						label: "Current Year Earnings",
						accessor: (d) => d.equity.currentYearEarnings,
					},
					{
						id: "distributions",
						label: "Distributions",
						accessor: (d) => d.equity.distributions,
					},
					{ id: "otherEquity", label: "Other Equity", accessor: (d) => d.equity.otherEquity },
					{
						id: "equityAdjustments",
						label: "Adjustments",
						accessor: (d) => d.equity.equityAdjustments,
						automated: true,
					},
				],
			},
		],
	},
];

const buildRollupCells = (
	accessor: RollupAccessor,
	periods: PeriodViewModel<BalanceSheetId>[],
	viewByPeriod: Map<BalanceSheetId, FinancialPeriod.BalanceSheetView>,
): Record<BalanceSheetId, FinancialViewCell> =>
	buildPeriodRecord(
		periods.map((p) => p.id),
		(periodId) => {
			const view = viewByPeriod.get(periodId);
			const rollup = view ? accessor(view) : null;
			return {
				given: rollup?.given ?? null,
				adjustment: null,
				allowed: rollup?.allowed ?? null,
			};
		},
	);

const emptyCell = (): FinancialViewCell => ({ given: null, adjustment: null, allowed: null });

const buildEmptyCells = (
	periods: PeriodViewModel<BalanceSheetId>[],
): Record<BalanceSheetId, FinancialViewCell> =>
	buildPeriodRecord(
		periods.map((p) => p.id),
		() => emptyCell(),
	);

const buildEmptyBooleanCells = (
	periods: PeriodViewModel<BalanceSheetId>[],
): Record<BalanceSheetId, BooleanCell> =>
	buildPeriodRecord(
		periods.map((p) => p.id),
		() => ({ given: null, adjustment: null, allowed: null }),
	);

type LineItemCellValues = {
	given: number | null;
	adjustment: FinancialPeriod.AdjustedLeaf | null;
	allowed: number | null;
};

const lineItemCellValues = (item: FinancialPeriod.LineItemView): LineItemCellValues => ({
	given: item.given ?? null,
	adjustment: item.adjusted ?? null,
	allowed: item.allowed ?? null,
});

const buildGroupRow = (
	group: GroupSpec,
	categoryId: string,
	periods: PeriodViewModel<BalanceSheetId>[],
	viewByPeriod: Map<BalanceSheetId, FinancialPeriod.BalanceSheetView>,
	colorMode: PpChangeColorMode,
): FinancialViewRow<BalanceSheetId> => {
	const labelToValuesByPeriod = new Map<string, Map<BalanceSheetId, LineItemCellValues>>();

	for (const period of periods) {
		const view = viewByPeriod.get(period.id);
		if (!view) continue;
		for (const item of group.accessor(view).lineItems) {
			let m = labelToValuesByPeriod.get(item.label);
			if (!m) {
				m = new Map();
				labelToValuesByPeriod.set(item.label, m);
			}
			m.set(period.id, lineItemCellValues(item));
		}
	}

	const groupRowId = `${categoryId}::${group.id}::group`;

	const leafRows: FinancialViewRow<BalanceSheetId>[] = [];
	for (const [label, valuesByPeriod] of labelToValuesByPeriod) {
		const cells = buildPeriodRecord(
			periods.map((p) => p.id),
			(periodId) => {
				const vals = valuesByPeriod.get(periodId) ?? {
					given: null,
					adjustment: null,
					allowed: null,
				};
				return vals;
			},
		);
		leafRows.push({
			id: `${groupRowId}::${label}`,
			level: 4,
			kind: "data",
			label,
			cells,
			ppChangeColorMode: colorMode,
			...(group.automated ? { editable: false } : {}),
		});
	}

	const groupCells = buildRollupCells((v) => group.accessor(v).total, periods, viewByPeriod);

	return {
		id: groupRowId,
		level: 3,
		kind: "category",
		label: group.label,
		cells: groupCells,
		allowsAddedLineItems: !group.automated,
		ppChangeColorMode: colorMode,
		children: leafRows,
	};
};

export function buildBalanceSheetViewModel(
	balanceSheets: BalanceSheetWithView[],
	financialPeriods: FinancialPeriodDto[],
): StatementMatrixViewModel<BalanceSheetId> {
	const periods: PeriodViewModel<BalanceSheetId>[] = balanceSheets.map((bs) => ({
		id: bs.id,
		label: DateTime.fromISO(bs.statementDate).toFormat("MM/dd/yyyy"),
		periodTypeLabel: periodTypeLabel(bs),
		periodEndDate: bs.statementDate,
		periodType: financialPeriods.find((fp) => fp.id === bs.financialPeriodId)?.periodType,
		preparationMethod: bs.preparationMethod,
		sourceStatementDate: bs.sourceStatementDate,
		sourceDataSource: bs.sourceDataSource,
		reviewedAt: bs.underwriterReviewedAt,
		fileId: bs.fileId,
		isActive: Boolean(financialPeriods.find((fp) => fp.activeBalanceSheetId === bs.id)),
	}));

	if (periods.length === 0) return { periods, rows: [] };

	const viewByPeriod = new Map<BalanceSheetId, FinancialPeriod.BalanceSheetView>();
	for (const bs of balanceSheets) {
		viewByPeriod.set(bs.id, bs.view);
	}

	const rows: FinancialViewRow<BalanceSheetId>[] = [];

	for (const section of SECTIONS) {
		const colorMode = sectionColorMode(section.id);
		const categoryRows: FinancialViewRow<BalanceSheetId>[] = [];
		for (const cat of section.categories) {
			const groupRows = cat.groups.map((group) =>
				buildGroupRow(
					group,
					cat.id,
					periods,
					viewByPeriod,
					NEUTRAL_GROUP_IDS.has(group.id) ? "neutral" : colorMode,
				),
			);
			const categoryCells = buildRollupCells(cat.totalAccessor, periods, viewByPeriod);
			categoryRows.push({
				id: cat.id,
				level: 2,
				kind: "category",
				label: cat.label,
				cells: categoryCells,
				hideValuesWhenExpanded: true,
				ppChangeColorMode: colorMode,
				children: [
					...groupRows,
					{
						id: `${cat.id}::__subtotal`,
						level: 3,
						kind: "subtotal",
						label: `Total ${cat.label}`,
						cells: categoryCells,
						indentOffset: -2,
						ppChangeColorMode: colorMode,
					},
				],
			});
		}
		if (categoryRows.length === 0) continue;

		const sectionCells = buildRollupCells(section.totalAccessor, periods, viewByPeriod);
		rows.push(...categoryRows);
		if (section.id !== "bs::equity")
			rows.push({
				id: section.subtotalId,
				level: 1,
				kind: "subtotal",
				label: section.subtotalLabel,
				cells: sectionCells,
				ppChangeColorMode: colorMode,
				derivedFrom: { kind: "sum_of_rows", rowIds: categoryRows.map((c) => c.id) },
			});
	}

	rows.push({
		id: TOTAL_LIABILITIES_AND_EQUITY_ID,
		level: 1,
		kind: "derived",
		label: "Total Liabilities & Equity",
		cells: buildEmptyCells(periods),
		ppChangeColorMode: "neutral",
		derivedFrom: { kind: "sum_of_rows", rowIds: [TOTAL_LIABILITIES_ID, TOTAL_EQUITY_ID] },
	});

	rows.push({
		id: BALANCED_ID,
		level: 1,
		kind: "derived",
		label: "Balanced",
		cells: buildEmptyCells(periods),
		booleanCells: buildEmptyBooleanCells(periods),
		derivedFrom: {
			kind: "equality_of_rows",
			rowIds: [TOTAL_ASSETS_ID, TOTAL_LIABILITIES_AND_EQUITY_ID],
		},
	});

	return { periods, rows };
}
